// src/utils/imageProcessing.js
// Real pixel-level image processing using Canvas API
// All operations mutate actual pixel data — no CSS tricks

// Extract raw pixel data from an image element via canvas
export const getImageData = (imgEl, maxDim = 600) => {
  const canvas = document.createElement('canvas');
  let w = imgEl.naturalWidth, h = imgEl.naturalHeight;
  if (w > maxDim || h > maxDim) {
    const scale = Math.min(maxDim / w, maxDim / h);
    w = Math.floor(w * scale); h = Math.floor(h * scale);
  }
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(imgEl, 0, 0, w, h);
  return { imageData: ctx.getImageData(0, 0, w, h), canvas, ctx, w, h };
};

// Apply a filter and return a new canvas URL
export const applyFilter = (imgEl, filterType, params = {}) => {
  const { imageData, canvas, ctx, w, h } = getImageData(imgEl);
  const data = imageData.data; // Uint8ClampedArray [R,G,B,A, R,G,B,A, ...]
  const out = new Uint8ClampedArray(data.length);

  switch (filterType) {
    case 'grayscale':
      for (let i = 0; i < data.length; i += 4) {
        const gray = 0.299 * data[i] + 0.587 * data[i+1] + 0.114 * data[i+2];
        out[i] = out[i+1] = out[i+2] = gray;
        out[i+3] = data[i+3];
      }
      break;

    case 'invert':
      for (let i = 0; i < data.length; i += 4) {
        out[i] = 255 - data[i]; out[i+1] = 255 - data[i+1]; out[i+2] = 255 - data[i+2];
        out[i+3] = data[i+3];
      }
      break;

    case 'brightness': {
      const factor = params.brightness ?? 1.2;
      for (let i = 0; i < data.length; i += 4) {
        out[i] = Math.min(255, data[i] * factor);
        out[i+1] = Math.min(255, data[i+1] * factor);
        out[i+2] = Math.min(255, data[i+2] * factor);
        out[i+3] = data[i+3];
      }
      break;
    }

    case 'contrast': {
      const factor = params.contrast ?? 1.5;
      const intercept = 128 * (1 - factor);
      for (let i = 0; i < data.length; i += 4) {
        out[i] = Math.min(255, Math.max(0, data[i] * factor + intercept));
        out[i+1] = Math.min(255, Math.max(0, data[i+1] * factor + intercept));
        out[i+2] = Math.min(255, Math.max(0, data[i+2] * factor + intercept));
        out[i+3] = data[i+3];
      }
      break;
    }

    case 'threshold': {
      const thresh = params.threshold ?? 128;
      for (let i = 0; i < data.length; i += 4) {
        const gray = 0.299 * data[i] + 0.587 * data[i+1] + 0.114 * data[i+2];
        const val = gray > thresh ? 255 : 0;
        out[i] = out[i+1] = out[i+2] = val; out[i+3] = data[i+3];
      }
      break;
    }

    case 'sepia':
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i], g = data[i+1], b = data[i+2];
        out[i]   = Math.min(255, r * 0.393 + g * 0.769 + b * 0.189);
        out[i+1] = Math.min(255, r * 0.349 + g * 0.686 + b * 0.168);
        out[i+2] = Math.min(255, r * 0.272 + g * 0.534 + b * 0.131);
        out[i+3] = data[i+3];
      }
      break;

    case 'red_channel':
      for (let i = 0; i < data.length; i += 4) {
        out[i] = data[i]; out[i+1] = 0; out[i+2] = 0; out[i+3] = data[i+3];
      }
      break;

    case 'green_channel':
      for (let i = 0; i < data.length; i += 4) {
        out[i] = 0; out[i+1] = data[i+1]; out[i+2] = 0; out[i+3] = data[i+3];
      }
      break;

    case 'blue_channel':
      for (let i = 0; i < data.length; i += 4) {
        out[i] = 0; out[i+1] = 0; out[i+2] = data[i+2]; out[i+3] = data[i+3];
      }
      break;

    case 'sharpen': {
      // 3×3 sharpening kernel
      const kernel = [0, -1, 0, -1, 5, -1, 0, -1, 0];
      applyKernel(data, out, kernel, w, h);
      break;
    }

    case 'blur': {
      // Box blur 3×3
      const kernel = [1/9,1/9,1/9, 1/9,1/9,1/9, 1/9,1/9,1/9];
      applyKernel(data, out, kernel, w, h);
      break;
    }

    case 'edge_detect': {
      // Sobel-like
      const kernel = [-1, -1, -1, -1, 8, -1, -1, -1, -1];
      applyKernel(data, out, kernel, w, h);
      break;
    }

    case 'emboss': {
      const kernel = [-2, -1, 0, -1, 1, 1, 0, 1, 2];
      applyKernel(data, out, kernel, w, h);
      // Add 128 bias
      for (let i = 0; i < out.length; i += 4) {
        out[i] = Math.min(255, Math.max(0, out[i] + 128));
        out[i+1] = Math.min(255, Math.max(0, out[i+1] + 128));
        out[i+2] = Math.min(255, Math.max(0, out[i+2] + 128));
      }
      break;
    }

    case 'rotate_hue': {
      const degrees = params.hue ?? 90;
      const rad = degrees * Math.PI / 180;
      const cos = Math.cos(rad), sin = Math.sin(rad);
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i]/255, g = data[i+1]/255, b = data[i+2]/255;
        out[i]   = Math.min(255, Math.max(0, (r*(cos+0.143*(1-cos)+0.140*sin) + g*(0.143*(1-cos)-0.140*sin) + b*(0.143*(1-cos)+0.283*sin))*255));
        out[i+1] = Math.min(255, Math.max(0, (r*(0.143*(1-cos)+0.140*sin) + g*(cos+0.140*(1-cos)) + b*(0.140*(1-cos)-0.283*sin))*255));
        out[i+2] = Math.min(255, Math.max(0, (r*(0.143*(1-cos)-0.283*sin) + g*(0.140*(1-cos)+0.283*sin) + b*(cos+0.140*(1-cos)))*255));
        out[i+3] = data[i+3];
      }
      break;
    }

    case 'pixelate': {
      const blockSize = params.blockSize ?? 10;
      // Copy data first
      for (let i = 0; i < data.length; i++) out[i] = data[i];
      for (let y = 0; y < h; y += blockSize) {
        for (let x = 0; x < w; x += blockSize) {
          let rSum = 0, gSum = 0, bSum = 0, count = 0;
          for (let dy = 0; dy < blockSize && y+dy < h; dy++) {
            for (let dx = 0; dx < blockSize && x+dx < w; dx++) {
              const idx = ((y+dy)*w + (x+dx))*4;
              rSum += data[idx]; gSum += data[idx+1]; bSum += data[idx+2]; count++;
            }
          }
          const ra = rSum/count, ga = gSum/count, ba = bSum/count;
          for (let dy = 0; dy < blockSize && y+dy < h; dy++) {
            for (let dx = 0; dx < blockSize && x+dx < w; dx++) {
              const idx = ((y+dy)*w + (x+dx))*4;
              out[idx] = ra; out[idx+1] = ga; out[idx+2] = ba; out[idx+3] = 255;
            }
          }
        }
      }
      break;
    }

    case 'noise': {
      const amount = params.noise ?? 40;
      for (let i = 0; i < data.length; i += 4) {
        const n = (Math.random() - 0.5) * amount * 2;
        out[i]   = Math.min(255, Math.max(0, data[i] + n));
        out[i+1] = Math.min(255, Math.max(0, data[i+1] + n));
        out[i+2] = Math.min(255, Math.max(0, data[i+2] + n));
        out[i+3] = data[i+3];
      }
      break;
    }

    default:
      for (let i = 0; i < data.length; i++) out[i] = data[i];
  }

  const newImageData = new ImageData(out, w, h);
  ctx.putImageData(newImageData, 0, 0);
  return canvas.toDataURL();
};

// Helper: apply 3×3 convolution kernel
const applyKernel = (src, dst, kernel, w, h) => {
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      let r = 0, g = 0, b = 0;
      for (let ky = -1; ky <= 1; ky++) {
        for (let kx = -1; kx <= 1; kx++) {
          const idx = ((y + ky) * w + (x + kx)) * 4;
          const k = kernel[(ky + 1) * 3 + (kx + 1)];
          r += src[idx] * k; g += src[idx+1] * k; b += src[idx+2] * k;
        }
      }
      const outIdx = (y * w + x) * 4;
      dst[outIdx]   = Math.min(255, Math.max(0, r));
      dst[outIdx+1] = Math.min(255, Math.max(0, g));
      dst[outIdx+2] = Math.min(255, Math.max(0, b));
      dst[outIdx+3] = src[outIdx+3];
    }
  }
};

// Get channel histograms from pixel data
export const getChannelHistograms = (imgEl) => {
  const { imageData, w, h } = getImageData(imgEl, 400);
  const data = imageData.data;
  const bins = 64;
  const r = Array(bins).fill(0), g = Array(bins).fill(0), b = Array(bins).fill(0), gray = Array(bins).fill(0);
  const total = w * h;

  for (let i = 0; i < data.length; i += 4) {
    const ri = Math.floor(data[i] / 256 * bins);
    const gi = Math.floor(data[i+1] / 256 * bins);
    const bi = Math.floor(data[i+2] / 256 * bins);
    const gv = Math.floor((0.299*data[i] + 0.587*data[i+1] + 0.114*data[i+2]) / 256 * bins);
    r[ri]++; g[gi]++; b[bi]++; gray[gv]++;
  }

  return Array.from({ length: bins }, (_, i) => ({
    bin: Math.floor(i * 256 / bins),
    R: r[i], G: g[i], B: b[i], Gray: gray[i],
    r_pct: +(r[i] / total * 100).toFixed(2),
    g_pct: +(g[i] / total * 100).toFixed(2),
    b_pct: +(b[i] / total * 100).toFixed(2),
  }));
};

// Get pixel-level stats per channel
export const getChannelStats = (imgEl) => {
  const { imageData } = getImageData(imgEl, 400);
  const data = imageData.data;
  const channels = { R: [], G: [], B: [], Luma: [] };

  for (let i = 0; i < data.length; i += 4) {
    channels.R.push(data[i]);
    channels.G.push(data[i+1]);
    channels.B.push(data[i+2]);
    channels.Luma.push(0.299*data[i] + 0.587*data[i+1] + 0.114*data[i+2]);
  }

  const stat = (arr) => {
    const mean = arr.reduce((s, v) => s + v, 0) / arr.length;
    const std = Math.sqrt(arr.reduce((s, v) => s + Math.pow(v - mean, 2), 0) / arr.length);
    const sorted = [...arr].sort((a, b) => a - b);
    return { mean: mean.toFixed(2), std: std.toFixed(2), min: sorted[0], max: sorted[sorted.length - 1], median: sorted[Math.floor(sorted.length / 2)] };
  };

  return {
    R: stat(channels.R),
    G: stat(channels.G),
    B: stat(channels.B),
    Luma: stat(channels.Luma),
  };
};

// Downsample image for pixel grid visualization
export const getPixelGrid = (imgEl, gridSize = 32) => {
  const canvas = document.createElement('canvas');
  canvas.width = gridSize; canvas.height = gridSize;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(imgEl, 0, 0, gridSize, gridSize);
  const data = ctx.getImageData(0, 0, gridSize, gridSize).data;
  const pixels = [];
  for (let y = 0; y < gridSize; y++) {
    for (let x = 0; x < gridSize; x++) {
      const i = (y * gridSize + x) * 4;
      pixels.push({ x, y, r: data[i], g: data[i+1], b: data[i+2], a: data[i+3] });
    }
  }
  return pixels;
};

// Get image metadata
export const getImageMetadata = (imgEl, file) => ({
  width: imgEl.naturalWidth,
  height: imgEl.naturalHeight,
  aspectRatio: (imgEl.naturalWidth / imgEl.naturalHeight).toFixed(2),
  megapixels: +((imgEl.naturalWidth * imgEl.naturalHeight) / 1_000_000).toFixed(2),
  fileSize: file ? (file.size / 1024).toFixed(1) + ' KB' : 'N/A',
  fileName: file?.name || 'sample',
  fileType: file?.type || 'image/jpeg',
});
